import React, { useState } from "react";

const AnalyticsPage = () => {
  const [dateRange, setDateRange] = useState("30days");

  // Mock data - replace with real API calls
  const analytics = {
    overview: {
      totalRevenue: 25450.5,
      totalOrders: 156,
      averageOrderValue: 163.14,
      conversionRate: 2.4,
    },
    salesData: [
      { date: "2024-01-01", revenue: 1200, orders: 8 },
      { date: "2024-01-02", revenue: 1500, orders: 12 },
      { date: "2024-01-03", revenue: 900, orders: 6 },
      { date: "2024-01-04", revenue: 1800, orders: 15 },
      { date: "2024-01-05", revenue: 2100, orders: 18 },
    ],
    topProducts: [
      { name: "Wireless Headphones", sales: 45, revenue: 13455 },
      { name: "Smart Watch", sales: 32, revenue: 7968 },
      { name: "Laptop Stand", sales: 28, revenue: 1399.72 },
      { name: "Phone Case", sales: 25, revenue: 499.75 },
    ],
    ordersByStatus: {
      processing: 8,
      confirmed: 142,
      shipped: 6,
      delivered: 0,
      cancelled: 0,
    },
    paymentMethods: {
      bank: 89,
      cod: 67,
    },
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-600">
            Sales performance and business insights
          </p>
        </div>
        <select
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-primary-600"
        >
          <option value="7days">Last 7 days</option>
          <option value="30days">Last 30 days</option>
          <option value="90days">Last 90 days</option>
          <option value="1year">Last year</option>
        </select>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <AnalyticsCard
          title="Total Revenue"
          value={`$${analytics.overview.totalRevenue.toLocaleString()}`}
          icon="💰"
          color="green"
          change="+12.5%"
        />
        <AnalyticsCard
          title="Total Orders"
          value={analytics.overview.totalOrders}
          icon="📦"
          color="blue"
          change="+8.2%"
        />
        <AnalyticsCard
          title="Average Order Value"
          value={`$${analytics.overview.averageOrderValue.toFixed(2)}`}
          icon="📊"
          color="purple"
          change="+3.1%"
        />
        <AnalyticsCard
          title="Conversion Rate"
          value={`${analytics.overview.conversionRate}%`}
          icon="🎯"
          color="yellow"
          change="+0.5%"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sales Chart */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Sales Overview
          </h2>
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
            <p className="text-gray-500">
              Sales chart would be rendered here using Recharts
            </p>
          </div>
        </div>

        {/* Order Status Breakdown */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Order Status
          </h2>
          <div className="space-y-3">
            {Object.entries(analytics.ordersByStatus).map(([status, count]) => (
              <div key={status} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div
                    className={`w-3 h-3 rounded-full mr-3 ${
                      status === "processing"
                        ? "bg-yellow-400"
                        : status === "confirmed"
                        ? "bg-green-400"
                        : status === "shipped"
                        ? "bg-blue-400"
                        : status === "delivered"
                        ? "bg-gray-400"
                        : "bg-red-400"
                    }`}
                  ></div>
                  <span className="text-sm capitalize text-gray-700">
                    {status}
                  </span>
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {count}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Top Products */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Top Performing Products
          </h2>
        </div>
        <div className="p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="text-left">
                  <th className="text-xs font-medium text-gray-500 uppercase tracking-wider pb-3">
                    Product
                  </th>
                  <th className="text-xs font-medium text-gray-500 uppercase tracking-wider pb-3">
                    Sales
                  </th>
                  <th className="text-xs font-medium text-gray-500 uppercase tracking-wider pb-3">
                    Revenue
                  </th>
                  <th className="text-xs font-medium text-gray-500 uppercase tracking-wider pb-3">
                    Performance
                  </th>
                </tr>
              </thead>
              <tbody className="space-y-3">
                {analytics.topProducts.map((product, index) => (
                  <tr key={index} className="border-t border-gray-100">
                    <td className="py-3">
                      <div className="font-medium text-gray-900">
                        {product.name}
                      </div>
                    </td>
                    <td className="py-3">
                      <div className="text-gray-900">{product.sales} units</div>
                    </td>
                    <td className="py-3">
                      <div className="font-medium text-gray-900">
                        ${product.revenue.toLocaleString()}
                      </div>
                    </td>
                    <td className="py-3">
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-primary-600 h-2 rounded-full"
                          style={{
                            width: `${Math.min(
                              (product.sales / 45) * 100,
                              100
                            )}%`,
                          }}
                        ></div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Payment Methods */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Payment Methods
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-blue-400 mr-3"></div>
                <span className="text-sm text-gray-700">Bank Transfer</span>
              </div>
              <span className="text-sm font-medium text-gray-900">
                {analytics.paymentMethods.bank} (
                {(
                  (analytics.paymentMethods.bank /
                    (analytics.paymentMethods.bank +
                      analytics.paymentMethods.cod)) *
                  100
                ).toFixed(1)}
                %)
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-green-400 mr-3"></div>
                <span className="text-sm text-gray-700">Cash on Delivery</span>
              </div>
              <span className="text-sm font-medium text-gray-900">
                {analytics.paymentMethods.cod} (
                {(
                  (analytics.paymentMethods.cod /
                    (analytics.paymentMethods.bank +
                      analytics.paymentMethods.cod)) *
                  100
                ).toFixed(1)}
                %)
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Key Metrics
          </h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Orders per day</span>
              <span className="text-sm font-medium text-gray-900">
                {(analytics.overview.totalOrders / 30).toFixed(1)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Revenue per day</span>
              <span className="text-sm font-medium text-gray-900">
                ${(analytics.overview.totalRevenue / 30).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Pending payments</span>
              <span className="text-sm font-medium text-gray-900">
                {analytics.ordersByStatus.processing}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Success rate</span>
              <span className="text-sm font-medium text-gray-900">
                {(
                  ((analytics.ordersByStatus.confirmed +
                    analytics.ordersByStatus.shipped +
                    analytics.ordersByStatus.delivered) /
                    analytics.overview.totalOrders) *
                  100
                ).toFixed(1)}
                %
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AnalyticsCard = ({ title, value, icon, color, change }) => {
  const colorClasses = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-green-50 text-green-600",
    yellow: "bg-yellow-50 text-yellow-600",
    purple: "bg-purple-50 text-purple-600",
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {change && (
            <p className="text-sm text-green-600 mt-1">
              {change} from last period
            </p>
          )}
        </div>
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <span className="text-2xl">{icon}</span>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
